import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ContactForm.scss';

const fields = [
  { name: 'name',    label: 'Full Name',     type: 'text',  placeholder: 'Your name',      required: true },
  { name: 'company', label: 'Company',       type: 'text',  placeholder: 'Company name',   required: false },
  { name: 'role',    label: 'Role',          type: 'text',  placeholder: 'Your role',      required: false },
  { name: 'email',   label: 'Email Address', type: 'email', placeholder: 'you@company.com',required: true },
  { name: 'whatsapp',label: 'WhatsApp Number',type: 'tel',  placeholder: '+2348012345678', required: false },
];

const E164_REGEX = /^\+[1-9]\d{7,14}$/;

export default function ContactForm() {
  const [values, setValues]   = useState({ name: '', company: '', role: '', email: '', whatsapp: '', message: '' });
  const [errors, setErrors]   = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!values.name.trim())    e.name    = 'Name is required';
    if (!values.email.trim())   e.email   = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) e.email = 'Enter a valid email';
    if (values.whatsapp.trim() && !E164_REGEX.test(values.whatsapp.trim())) {
      e.whatsapp = 'Use international format, e.g. +2348012345678';
    }
    if (!values.message.trim()) e.message = 'Message is required';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues(v => ({ ...v, [name]: value }));
    if (submitError) setSubmitError('');
    if (errors[name]) setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitError('');
    setLoading(true);
    try {
      const endpoint = process.env.REACT_APP_CONTACT_ENDPOINT || '/api/contact';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name.trim(),
          company: values.company.trim(),
          role: values.role.trim(),
          email: values.email.trim(),
          whatsapp: values.whatsapp.trim(),
          message: values.message.trim(),
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Unable to send message right now');
      }

      setValues({ name: '', company: '', role: '', email: '', whatsapp: '', message: '' });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Unable to send message right now');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        className="contact-form__success"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <CheckIcon />
        <p>Message received — we'll be in touch shortly.</p>
      </motion.div>
    );
  }

  return (
    <form className="contact-form" onSubmit={handleSubmit} noValidate>
      <div className="contact-form__grid">
        {fields.map(({ name, label, type, placeholder }) => (
          <div key={name} className="contact-form__field">
            <label className="contact-form__label">{label}</label>
            <input
              name={name}
              type={type}
              placeholder={placeholder}
              value={values[name]}
              onChange={handleChange}
              className={`contact-form__input${errors[name] ? ' error' : ''}`}
            />
            <AnimatePresence>
              {errors[name] && (
                <motion.span
                  className="contact-form__error"
                  initial={{ opacity: 0, y: -3 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                >
                  {errors[name]}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        ))}

        <div className="contact-form__field contact-form__field--full">
          <label className="contact-form__label">Message</label>
          <textarea
            name="message"
            placeholder="Tell us about your business, corridor, or what you're exploring…"
            value={values.message}
            onChange={handleChange}
            className={`contact-form__textarea${errors.message ? ' error' : ''}`}
          />
          <AnimatePresence>
            {errors.message && (
              <motion.span
                className="contact-form__error"
                initial={{ opacity: 0, y: -3 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              >
                {errors.message}
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {submitError && (
          <motion.p
            className="contact-form__submit-error"
            initial={{ opacity: 0, y: -3 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            {submitError}
          </motion.p>
        )}
      </AnimatePresence>

      <div className="contact-form__submit">
        <motion.button
          type="submit"
          className="btn-primary"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={loading}
        >
          {loading ? 'Sending…' : 'Send Message'}
          {!loading && <ArrowIcon />}
        </motion.button>
      </div>
    </form>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="9" cy="9" r="8" stroke="#6B7280" strokeWidth="1.4"/>
      <path d="M5.5 9l2.5 2.5 4.5-4.5" stroke="#6B7280" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <path d="M2.5 7h9M8 3.5L11.5 7 8 10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
